'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { FileText, Download, Clock, Brain, AlertTriangle, Trash2, Eye, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { useChat } from '@/lib/hooks/useChat';
import { getReports, saveReport, deleteReport, GeneratedReport, getRiskEvents } from '@/lib/storage';
import { DEFAULT_RISK_EVENTS } from '@/lib/riskData';
import { getCompanyAnalyses } from '@/lib/storage';
import toast from 'react-hot-toast';

const severityColor: Record<string, string> = {
  Critical: 'text-danger bg-danger/10',
  High: 'text-warning bg-warning/10',
  Medium: 'text-info bg-info/10',
  Low: 'text-success bg-success/10',
};

function buildReportPrompt(riskEvents: typeof DEFAULT_RISK_EVENTS, companyName: string, companyProfile: string): string {
  const critical = riskEvents.filter(e => e.severity === 'Critical');
  const high = riskEvents.filter(e => e.severity === 'High');
  const escalating = riskEvents.filter(e => e.status === 'Escalating');
  const sanctions = riskEvents.filter(e => e.category === 'Sanctions');
  const cyber = riskEvents.filter(e => e.category === 'Cyber');
  const maritime = riskEvents.filter(e => e.category === 'Maritime');
  const supplyChain = riskEvents.filter(e => e.category === 'Supply Chain');

  return `You are a senior geopolitical and operational risk analyst at a top-tier intelligence consultancy. Generate a structured, professional AI risk intelligence report for ${companyName}.

COMPANY PROFILE:
${companyProfile}

LIVE RISK DATA (current dashboard state):
- Total active events: ${riskEvents.length}
- Critical severity: ${critical.length} events
- High severity: ${high.length} events
- Escalating status: ${escalating.length} events
- Sanctions events: ${sanctions.length} (${sanctions.map(e => e.event).join('; ')})
- Cyber events: ${cyber.length} (${cyber.map(e => e.event).join('; ')})
- Maritime events: ${maritime.length} (${maritime.map(e => e.event).join('; ')})
- Supply chain events: ${supplyChain.length} (${supplyChain.map(e => e.event).join('; ')})

KEY ACTIVE THREATS:
${riskEvents.slice(0, 6).map(e => `- [${e.severity}] ${e.event} | Region: ${e.region} | Sector: ${e.sector} | Escalation: ${e.escalationProb}% | Status: ${e.status}`).join('\n')}

Generate a structured intelligence report with EXACTLY these 5 sections. Use professional, consultancy-grade language. Be specific, actionable, and tie all analysis to ${companyName}'s operational exposure. Avoid generic commentary.

Format your response as valid JSON with this exact structure:
{
  "executiveSummary": "2-3 paragraph executive summary covering overall risk posture, key threats, and ${companyName} exposure",
  "threatAssessment": "Detailed breakdown of sanctions risk, cyber threats, political instability, maritime disruption, and supply chain exposure — with severity ratings and confidence levels",
  "exposureAnalysis": "Specific analysis of how current threats affect ${companyName}'s suppliers, jurisdictions, logistics routes, counterparties, and operational regions",
  "escalationForecast": "7-14 day outlook: likelihood of escalation for each major threat, key drivers, warning indicators, and probability estimates",
  "recommendedActions": "Specific, prioritised actions for compliance teams, risk teams, and operations teams — formatted as numbered action items"
}`;
}

export default function AIReportsPage() {
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [viewingReport, setViewingReport] = useState<GeneratedReport | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  useEffect(() => {
    const stored = getReports();
    setReports(stored);
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error.message);
      setIsGenerating(false);
      setGenerationError(error.message);
    }
  }, [error]);

  useEffect(() => {
    if (response && !isLoading && isGenerating) {
      try {
        // Extract JSON from response (handle markdown code blocks)
        let jsonStr = response;
        const jsonMatch = response.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (jsonMatch) jsonStr = jsonMatch[1];
        const parsed = JSON.parse(jsonStr);

        const riskEvents = (() => {
          const stored = getRiskEvents();
          return stored.length > 0 ? stored : DEFAULT_RISK_EVENTS;
        })();

        const analyses = getCompanyAnalyses();
        const activeCompany = analyses[0];
        const companyName = activeCompany?.companyName || 'Acme Corp';

        const critical = riskEvents.filter(e => e.severity === 'Critical').length;
        const high = riskEvents.filter(e => e.severity === 'High').length;
        const escalating = riskEvents.filter(e => e.status === 'Escalating').length;
        const sanctions = riskEvents.filter(e => e.category === 'Sanctions').length;
        const overallScore = Math.round(
          (critical * 25 + high * 15 + escalating * 10 + sanctions * 20) / riskEvents.length
        );

        const newReport: GeneratedReport = {
          id: `report-${Date.now()}`,
          title: `${companyName} — AI Risk Intelligence Briefing`,
          type: 'AI-Generated Report',
          generatedAt: new Date().toISOString(),
          companyProfile: activeCompany ? `${companyName} | Suppliers: ${activeCompany.suppliers.join(', ')} | Regions: ${activeCompany.regions.join(', ')}` : `${companyName} | Global operations`,
          companyName,
          riskSnapshot: {
            criticalEvents: critical,
            highEvents: high,
            escalatingEvents: escalating,
            sanctionsMatches: sanctions,
            overallRiskScore: Math.min(overallScore, 100),
          },
          content: {
            executiveSummary: parsed.executiveSummary || '',
            threatAssessment: parsed.threatAssessment || '',
            exposureAnalysis: parsed.exposureAnalysis || '',
            escalationForecast: parsed.escalationForecast || '',
            recommendedActions: parsed.recommendedActions || '',
          },
          severity: critical > 2 ? 'Critical' : high > 3 ? 'High' : 'Medium',
        };

        saveReport(newReport);
        setReports(getReports());
        setIsGenerating(false);
        setGenerationError(null);
        setViewingReport(newReport);
        toast.success('Intelligence report generated successfully');
      } catch {
        setIsGenerating(false);
        setGenerationError('Failed to parse AI response. Please try again.');
        toast.error('Report generation failed — please retry');
      }
    }
  }, [response, isLoading, isGenerating]);

  const handleGenerate = useCallback(() => {
    setIsGenerating(true);
    setGenerationError(null);

    const riskEvents = (() => {
      const stored = getRiskEvents();
      return stored.length > 0 ? stored : DEFAULT_RISK_EVENTS;
    })();

    const analyses = getCompanyAnalyses();
    const activeCompany = analyses[0];
    const companyName = activeCompany?.companyName || 'Acme Corp';
    const companyProfile = activeCompany
      ? `Company: ${companyName}\nSuppliers: ${activeCompany.suppliers.join(', ')}\nOperating Regions: ${activeCompany.regions.join(', ')}` : `${companyName} | Global operations`;

    const prompt = buildReportPrompt(riskEvents, companyName, companyProfile);

    sendMessage([
      { role: 'system', content: 'You are a senior geopolitical risk analyst. Always respond with valid JSON only — no markdown, no preamble, no explanation outside the JSON structure.' },
      { role: 'user', content: prompt },
    ], { max_completion_tokens: 2000 });
  }, [sendMessage]);

  const handleDelete = (id: string) => {
    deleteReport(id);
    setReports(getReports());
    if (viewingReport?.id === id) setViewingReport(null);
    toast.success('Report deleted');
  };

  const handleDownload = (report: GeneratedReport) => {
    const content = `GLOBALATLAS AI INTELLIGENCE REPORT
Generated: ${new Date(report.generatedAt).toLocaleString('en-GB')}
Company: ${report.companyName}
Severity: ${report.severity}
Risk Snapshot: ${report.riskSnapshot.criticalEvents} critical, ${report.riskSnapshot.highEvents} high, ${report.riskSnapshot.escalatingEvents} escalating

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. EXECUTIVE SUMMARY
${report.content.executiveSummary}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

2. THREAT ASSESSMENT
${report.content.threatAssessment}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

3. EXPOSURE ANALYSIS
${report.content.exposureAnalysis}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

4. ESCALATION FORECAST (7-14 DAYS)
${report.content.escalationForecast}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

5. RECOMMENDED ACTIONS
${report.content.recommendedActions}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
GlobalAtlas AI Intelligence Platform · globalatlas.co.uk · Beta
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GlobalAtlas_Report_${report.companyName.replace(/\s+/g, '_')}_${new Date(report.generatedAt).toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  return (
    <AppLayout activeRoute="/ai-reports">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">AI Intelligence Reports</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Analyst-style briefings generated from live threat data — company-specific relevance included
            </p>
          </div>
          <button
            onClick={handleGenerate}
            disabled={isGenerating || isLoading}
            className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-md hover:opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isGenerating || isLoading ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                <Brain size={14} />
                Generate New Report
              </>
            )}
          </button>
        </div>

        {/* Generation status */}
        {(isGenerating || isLoading) && (
          <div className="card-elevated rounded-xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-center gap-3">
              <RefreshCw size={15} className="text-primary animate-spin flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Generating intelligence report...</p>
                <p className="text-xs text-muted-foreground mt-0.5">AI is analysing live risk data, sanctions exposure, and company profile. This may take 15–30 seconds.</p>
              </div>
            </div>
          </div>
        )}

        {generationError && (
          <div className="card-elevated rounded-xl border border-danger/20 bg-danger/5 p-4">
            <div className="flex items-center gap-3">
              <XCircle size={15} className="text-danger flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Report generation failed</p>
                <p className="text-xs text-muted-foreground mt-0.5">{generationError}</p>
              </div>
              <button onClick={() => setGenerationError(null)} className="ml-auto text-muted-foreground hover:text-foreground">
                <XCircle size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Viewing a report */}
        {viewingReport && (
          <div className="card-elevated rounded-xl border border-primary/20 bg-card">
            <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FileText size={15} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{viewingReport.title}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Clock size={10} />
                    {new Date(viewingReport.generatedAt).toLocaleString('en-GB')}
                    <span className="mx-1">·</span>
                    <span className={`px-1.5 py-0.5 rounded text-2xs font-semibold ${severityColor[viewingReport.severity]}`}>{viewingReport.severity}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleDownload(viewingReport)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-3 py-1.5 hover:bg-muted transition-colors">
                  <Download size={12} />
                  Download
                </button>
                <button onClick={() => setViewingReport(null)} className="text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-3 py-1.5 hover:bg-muted transition-colors">
                  Close
                </button>
              </div>
            </div>

            {/* Risk Snapshot */}
            <div className="p-4 border-b border-border bg-muted/10">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Risk Snapshot at Generation</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: 'Critical Events', value: viewingReport.riskSnapshot.criticalEvents, color: 'text-danger' },
                  { label: 'High Events', value: viewingReport.riskSnapshot.highEvents, color: 'text-warning' },
                  { label: 'Escalating', value: viewingReport.riskSnapshot.escalatingEvents, color: 'text-warning' },
                  { label: 'Sanctions', value: viewingReport.riskSnapshot.sanctionsMatches, color: 'text-info' },
                  { label: 'Risk Score', value: `${viewingReport.riskSnapshot.overallRiskScore}`, color: 'text-foreground' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="text-center">
                    <p className={`text-xl font-bold font-tabular ${color}`}>{value}</p>
                    <p className="text-2xs text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Report Sections */}
            <div className="divide-y divide-border">
              {[
                { key: 'executiveSummary', label: '1. Executive Summary', icon: '📋' },
                { key: 'threatAssessment', label: '2. Threat Assessment', icon: '⚠️' },
                { key: 'exposureAnalysis', label: '3. Exposure Analysis', icon: '🎯' },
                { key: 'escalationForecast', label: '4. Escalation Forecast (7–14 Days)', icon: '📈' },
                { key: 'recommendedActions', label: '5. Recommended Actions', icon: '✅' },
              ].map(({ key, label, icon }) => (
                <div key={key} className="p-4">
                  <p className="text-sm font-semibold text-foreground mb-2">{icon} {label}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {viewingReport.content[key as keyof typeof viewingReport.content]}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Report History */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-foreground">Report History</p>
            <span className="text-xs text-muted-foreground">{reports.length} report{reports.length !== 1 ? 's' : ''} saved</span>
          </div>

          {reports.length === 0 ? (
            <div className="card-elevated rounded-xl border border-border p-8 text-center">
              <Brain size={32} className="text-muted-foreground mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium text-foreground mb-1">No reports generated yet</p>
              <p className="text-xs text-muted-foreground mb-4">Click "Generate New Report" to create your first AI intelligence briefing from live dashboard data.</p>
              <button onClick={handleGenerate} disabled={isGenerating || isLoading} className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-md hover:opacity-90 transition-all mx-auto disabled:opacity-60">
                <Brain size={14} />
                Generate First Report
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map(report => (
                <div key={report.id} className="card-elevated rounded-xl border border-border hover:bg-muted/20 transition-colors">
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <FileText size={15} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground mb-1">{report.title}</p>
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <span className={`text-2xs px-1.5 py-0.5 rounded font-medium ${severityColor[report.severity]}`}>{report.severity}</span>
                              <span className="text-2xs text-muted-foreground">{report.type}</span>
                              <span className="text-2xs text-muted-foreground flex items-center gap-1">
                                <Clock size={9} />
                                {new Date(report.generatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                              {report.content.executiveSummary.slice(0, 200)}...
                            </p>
                            <div className="flex items-center gap-1.5 mt-2">
                              <AlertTriangle size={11} className="text-warning flex-shrink-0" />
                              <p className="text-2xs text-warning font-medium">
                                {report.riskSnapshot.criticalEvents} critical · {report.riskSnapshot.escalatingEvents} escalating · {report.riskSnapshot.sanctionsMatches} sanctions
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => setViewingReport(viewingReport?.id === report.id ? null : report)}
                              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-3 py-1.5 hover:bg-muted transition-colors"
                            >
                              <Eye size={12} />
                              View
                            </button>
                            <button
                              onClick={() => handleDownload(report)}
                              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-3 py-1.5 hover:bg-muted transition-colors"
                            >
                              <Download size={12} />
                              Export
                            </button>
                            <button
                              onClick={() => handleDelete(report.id)}
                              className="flex items-center gap-1.5 text-xs text-danger/70 hover:text-danger border border-border rounded-md px-3 py-1.5 hover:bg-danger/10 transition-colors"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info note */}
        <div className="card-elevated rounded-xl border border-border p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={14} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="text-primary font-medium">AI-powered:</span> Reports are generated in real-time using live dashboard risk data, active alerts, sanctions exposure, and your company profile. Each report is saved to your history and can be exported as a text file. Reports are stored locally in your browser session.
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
